import { useCallback } from "react";

import { v4 } from "uuid";

import User from "@mapmycustomers/shared/types/User";

import CardType from "@app/enum/dashboard/CardType";
import getCardByType from "@app/scene/dashboard/utils/getCardByType";
import CardConfiguration from "@app/types/dashboard/CardConfiguration";
import Dashboard from "@app/types/dashboard/Dashboard";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";

import findInsertPosition from "./findInsertPosition";
import useGridCards from "./useGridCards";

const useAddCard = (
  boards: Dashboard[],
  currentUser: User,
  scope: Scope,
  onAddCard?: (payload: { card: DashboardCard; scope: Scope }) => void
) => {
  const cards = useGridCards(boards, scope, currentUser);

  const handleAddCard = useCallback(
    (
      type: CardType,
      configuration: CardConfiguration<any, any, any>,
      position?: Pick<ReactGridLayout.Layout, "x" | "y">,
      fullWidth?: boolean
    ) => {
      const boardLayout = cards.map(({ layout }) => layout);

      const cardDescriptor = getCardByType(type);
      if (!cardDescriptor) {
        return;
      }

      // TODO: add view state, probably reading it from the store?
      const viewState = {};

      const { h, w } = cardDescriptor.getCardDimensions({
        // TODO: think about how to avoid ts-ignore
        // this is safe since configuration is exactly for the dashboardCard type which we used to get cardDescriptor
        // @ts-ignore
        configuration,
        scope,
        viewState,
      });

      const { x, y } = findInsertPosition(boardLayout, { h, w });
      const now = new Date().toISOString();
      const newCard: DashboardCard = {
        id: v4(),
        configuration,
        createdAt: now,
        createdBy: { id: currentUser.id },
        fullWidth,
        type,
        updatedAt: now,
        updatedBy: { id: currentUser.id },
        x: position?.x ?? x,
        y: position?.y ?? y,
      };
      onAddCard?.({ card: newCard, scope });
    },
    [cards, currentUser, onAddCard, scope]
  );

  return handleAddCard;
};

export default useAddCard;
